import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab, ToggleButton, ToggleButtonGroup, Grid, Typography, InputAdornment, IconButton } from '@mui/material';
import { Icon } from 'components';
import { SERVICE_ICON_LIST, SERVICE_ICON_POSSESSIONS } from '../../pages/integrations/calendars/service-icons';
import { SearchIcon, CloseIcon } from 'components/mui';
const ICON_VARIANTS = {
  regular: 'Regular',
  light: 'Light',
  duotone: 'Filled',
  all: 'All'
};

// Possession filter is orthogonal to the variant tabs and defaults to 'all', so every icon
// that was selectable before CIN-6779 is still selectable without touching the new control.
const DEFAULT_POSSESSION = 'all';
export const IconPicker = ({
  value,
  onChange,
  onClose,
  label = 'Select Icon',
  size = 24,
  disabled = false,
  autoOpen = false
}) => {
  const [open, setOpen] = useState(autoOpen);
  const [selectedTab, setSelectedTab] = useState('regular');
  const [selectedPossession, setSelectedPossession] = useState(DEFAULT_POSSESSION);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter icons based on selected tab, possession type and search term
  const filteredIcons = useMemo(() => {
    let icons = SERVICE_ICON_LIST;

    // Filter by variant (tab)
    if (selectedTab !== 'all') {
      icons = icons.filter(icon => {
        if (selectedTab === 'duotone') {
          return icon.name.includes('fa-duotone-');
        }
        return icon.name.includes(`fa-${selectedTab}-`);
      });
    }

    // Filter by possession type - composes with the variant tab, it does not replace it
    if (selectedPossession !== 'all') {
      icons = icons.filter(icon => icon.possession === selectedPossession);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      icons = icons.filter(icon => icon.name.toLowerCase().includes(search) || icon.label.toLowerCase().includes(search) || icon.category.toLowerCase().includes(search));
    }
    return icons;
  }, [selectedTab, selectedPossession, searchTerm]);
  const handleOpen = () => {
    if (!disabled) {
      setOpen(true);
    }
  };
  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
    setSelectedTab('regular');
    setSelectedPossession(DEFAULT_POSSESSION);
    // Optional: lets an autoOpen parent unmount/reset its own flag so the picker can be reopened.
    if (onClose) {
      onClose();
    }
  };
  const handleIconSelect = iconName => {
    onChange(iconName);
    handleClose();
  };
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Exclusive ToggleButtonGroup emits null when the active button is clicked again -
  // ignore that so the grid can never end up filtered by an empty possession value.
  const handlePossessionChange = (event, newValue) => {
    if (newValue) {
      setSelectedPossession(newValue);
    }
  };
  const clearSearch = () => {
    setSearchTerm('');
  };

  // Get display icon (fallback to default if value doesn't exist)
  const displayIcon = value || 'fa-light-car-wrench';
  return <>
      {/* Icon Display Button - only show when not auto-opening */}
      {!autoOpen && <Box>
          <Typography variant="body2" color="text.secondary" sx={{
        mb: 1
      }}>
            {label}
          </Typography>
          <Button onClick={handleOpen} disabled={disabled} variant="outlined" data-testid="icon-picker-trigger" sx={{
        'display': 'flex',
        'alignItems': 'center',
        'gap': 2,
        'justifyContent': 'flex-start',
        'textTransform': 'none',
        'minHeight': '48px',
        'borderColor': '#ddd',
        '&:hover': {
          borderColor: '#3898D9'
        }
      }} fullWidth>
            <Box sx={{
          'color': '#666',
          '& .icon': {
            fill: 'currentColor'
          }
        }}>
              <Icon name={displayIcon} size={size} />
            </Box>
            <Box sx={{
          textAlign: 'left',
          flex: 1
        }}>
              <Typography variant="body2">
                {value ? SERVICE_ICON_LIST.find(icon => icon.name === value)?.label || value : 'Click to select icon'}
              </Typography>
              {value && <Typography variant="caption" color="text.secondary">
                  {value}
                </Typography>}
            </Box>
          </Button>
        </Box>}

      {/* Icon Picker Modal */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{
      sx: {
        minHeight: '70vh'
      }
    }}>
        <DialogTitle>
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
            <Typography variant="h5">Select Service Icon</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{
          mb: 3
        }}>
            {/* Tabs */}
            <Tabs value={selectedTab} onChange={handleTabChange} sx={{
            mb: 2,
            borderBottom: 1,
            borderColor: 'divider'
          }}>
              {Object.entries(ICON_VARIANTS).map(([key, label]) => <Tab key={key} label={label} value={key} />)}
            </Tabs>

            {/* Possession type filter - second control row, orthogonal to the variant tabs */}
            <Typography variant="caption" color="text.secondary" sx={{
            display: 'block',
            mb: 1
          }}>
              Business type
            </Typography>
            <ToggleButtonGroup value={selectedPossession} exclusive size="small" onChange={handlePossessionChange} aria-label="Filter icons by business type" data-testid="icon-possession-filter" sx={{
            mb: 2,
            flexWrap: 'wrap'
          }}>
              {SERVICE_ICON_POSSESSIONS.map(({
              id,
              label
            }) => <ToggleButton key={id} value={id} sx={{
              textTransform: 'none'
            }} data-testid={`icon-possession-filter-${id}`}>
                  {label}
                </ToggleButton>)}
            </ToggleButtonGroup>

            {/* Search Box */}
            <TextField fullWidth placeholder="Search icons..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} InputProps={{
            startAdornment: <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>,
            endAdornment: searchTerm && <InputAdornment position="end">
                    <IconButton size="small" onClick={clearSearch}>
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
          }} sx={{
            mb: 3
          }} />
          </Box>

          {/* Icons Grid */}
          <Box sx={{
          minHeight: '400px',
          maxHeight: '400px',
          overflow: 'auto'
        }}>
            {filteredIcons.length === 0 ? <Box sx={{
            textAlign: 'center',
            py: 4
          }} data-testid="icon-picker-empty">
                <Typography color="text.secondary">
                  {searchTerm ? `No icons found matching "${searchTerm}"` : 'No icons available for the selected style and business type'}
                </Typography>
              </Box> : <Grid container spacing={2}>
                {filteredIcons.map(iconData => <Grid item xs={6} sm={4} md={3} key={iconData.name}>
                    <Box sx={{
                'cursor': 'pointer',
                'textAlign': 'center',
                'p': 2,
                'borderRadius': 1,
                'backgroundColor': value === iconData.name ? '#f0f8ff' : 'transparent',
                'border': value === iconData.name ? '2px solid #3898D9' : '2px solid transparent',
                '&:hover': {
                  backgroundColor: '#f8f9fa'
                },
                'transition': 'all 0.2s ease'
              }} onClick={() => handleIconSelect(iconData.name)} title={iconData.label} data-testid={`service-icon-option-${iconData.name}`}>
                      <Icon name={iconData.name} size={24} />
                    </Box>
                  </Grid>)}
              </Grid>}
          </Box>
        </DialogContent>

        <DialogActions sx={{
        p: 3
      }}>
          <Button onClick={handleClose}>Cancel</Button>
          {value && <Button onClick={() => handleIconSelect('')} color="secondary">
              Clear Selection
            </Button>}
        </DialogActions>
      </Dialog>
    </>;
};
IconPicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  label: PropTypes.string,
  size: PropTypes.number,
  disabled: PropTypes.bool,
  autoOpen: PropTypes.bool
};